// predicateHeadCapabilities — predicate-head plan P3 (PH-D2). The per-engine
// capability table that governs `deriveCompletionContext`'s FOURTH slot-seam
// gate: a grammar-recognised `predicate-head` slot classifies only when the
// engine's capability set contains the slot's role AND (the clause is in the
// role's override set OR — for where-head/having-head only — the clause
// machine yielded `'unknown'`) — the capability check governs BOTH paths,
// mirroring `valueSlotCapabilities.ts`.
//
// P3 shipped the table EMPTY for every engine (the gate dormant — zero
// production classification changes; `slotNoWorse.test.ts` pins it). Each
// per-engine flip task adds its role(s) to the engine's set once the
// engine's resolver branch + policy wiring have landed. P7 flipped pgsql;
// P8a flipped mssql; P8b flipped mysql; P8c flipped oracle; P8d flipped
// sqlite — all five for all three head roles. No engine is pending. Tests
// exercise the gate ahead of a (hypothetical future) flip, or to force a
// LIVE engine back off, through the seam below
// (`__setPredicateHeadCapabilitiesForTest`), clearing it in `afterEach`.

import type { DatabaseEngineId } from '@/core/engines';
import type { PredicateHeadRole, SqlClause } from '@/features/sql-grammar/model';

const EMPTY_ROLES: ReadonlySet<PredicateHeadRole> = new Set<PredicateHeadRole>();

/** Production capability table. pgsql went LIVE for all three head roles at
 *  P7 (first per-engine flip); mssql followed at P8a; mysql at P8b; oracle
 *  at P8c; sqlite at P8d — the last of the five. Every engine now carries
 *  all three head roles; no engine is pending. */
export const PREDICATE_HEAD_CAPABILITIES: ReadonlyMap<
  DatabaseEngineId,
  ReadonlySet<PredicateHeadRole>
> = new Map<DatabaseEngineId, ReadonlySet<PredicateHeadRole>>([
  ['pgsql', new Set<PredicateHeadRole>(['where-head', 'having-head', 'on-head'])],
  ['mssql', new Set<PredicateHeadRole>(['where-head', 'having-head', 'on-head'])],
  ['mysql', new Set<PredicateHeadRole>(['where-head', 'having-head', 'on-head'])],
  ['oracle', new Set<PredicateHeadRole>(['where-head', 'having-head', 'on-head'])],
  ['sqlite', new Set<PredicateHeadRole>(['where-head', 'having-head', 'on-head'])],
]);

/** Clauses where a role's slot result may override a confidently-named clause
 *  classification (PH-D2). Each head role overrides exactly the clause it
 *  names: the recognizer is syntax-only (`ON` fires wherever the keyword
 *  directly precedes the cursor — MERGE actions, CREATE INDEX targets, …), so
 *  clause legality lives HERE, mirroring the value-slot layer split.
 *
 *  `'delete-where'` is absent because it is UNREACHABLE at a head, not because
 *  DELETE is excluded: `editorContextClause.ts` sets that clause on the DELETE
 *  keyword itself and then re-enters `'where'` on the WHERE keyword, so
 *  `DELETE FROM t WHERE ▮` arrives here as plain `'where'` and IS covered by
 *  the where-head entry. (`predicateHeadGate.test.ts` pins this — an earlier
 *  revision of this comment claimed the opposite and was wrong.)
 *
 *  Unknown-clause positions pass under the same capability check via the
 *  gate's unknown-clause arm — EXCEPT `'on-head'`, which is barred from that
 *  arm entirely (PH-D2 amendment, C1); see `deriveContext.ts`. */
export const PREDICATE_HEAD_OVERRIDE_CLAUSES: ReadonlyMap<
  PredicateHeadRole,
  ReadonlySet<SqlClause>
> = new Map<PredicateHeadRole, ReadonlySet<SqlClause>>([
  ['where-head', new Set<SqlClause>(['where'])],
  ['having-head', new Set<SqlClause>(['having'])],
  ['on-head', new Set<SqlClause>(['join-on'])],
]);

let capabilitiesTestOverride:
  | ReadonlyMap<DatabaseEngineId, ReadonlySet<PredicateHeadRole>>
  | null = null;

/** P3 test seam: replace the production table for the duration of a test.
 *  Pass `null` to restore it — callers MUST clear in `afterEach`. */
export function __setPredicateHeadCapabilitiesForTest(
  map: ReadonlyMap<DatabaseEngineId, ReadonlySet<PredicateHeadRole>> | null,
): void {
  capabilitiesTestOverride = map;
}

/** The active role set for `engineId` — the test override when installed,
 *  the production table otherwise. Missing engines resolve to the empty set. */
export function predicateHeadRolesForEngine(
  engineId: DatabaseEngineId,
): ReadonlySet<PredicateHeadRole> {
  return (capabilitiesTestOverride ?? PREDICATE_HEAD_CAPABILITIES).get(engineId) ?? EMPTY_ROLES;
}
